import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button } from '@/components/ui/primitives';
import { LoadingState } from '@/components/DataState';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { getSeoAutopilot, resumeSeoAutopilotListing, saveSeoAutopilotSettings } from '@/data/seo-autopilot';
import {
  CADENCE_LIMITS, STOP_LABELS, settingsError, settingsPatch, tierLabel, toggleIn,
  type SeoAutopilotCadence, type SeoAutopilotSettings, type SeoAutopilotStatus,
} from '@/data/seo-autopilot-model';
import { useWidth } from '@/pages/social/library/useWidth';
import { formatDayTime, todayParts, iso } from '@/pages/social/library/libraryModel';
import {
  CADENCE_PLAIN, PAGE_STATE_LABEL, alertLabel, dayLabel, describeAlert, describeChange, reasonLabel, filterSortPages, nextDraftDates, pageKind, pagePath, pageState, scheduleToday,
  type PageSort, type PageState,
} from '@/pages/social/seo-autopilot/autopilotView';

/* ----------------------------------------------------------------------------
 * SEO autopilot panel — redesigned (owner 25-10-2026: "the settings ui is 4th
 * grade! Not event visible and doesnt make it user friendly or modern").
 * Status card (Running/Stopped switch, today, this week, est. monthly cost,
 * needs attention) + Overview | Schedule | Pages | Settings | Activity.
 * PRESENTATION ONLY: the same GET /v1/content/seo-autopilot, POST …/settings
 * (validated by settingsError, minimal settingsPatch) and POST …/resume as
 * before. Every setting the old panel had is still here. No backend change.
 * -------------------------------------------------------------------------- */

const KEY = ['seo-autopilot'] as const;
type Tab = 'overview' | 'schedule' | 'pages' | 'settings' | 'activity';
const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'overview', label: 'Overview' }, { key: 'schedule', label: 'Schedule' }, { key: 'pages', label: 'Pages' },
  { key: 'settings', label: 'Settings' }, { key: 'activity', label: 'Activity' },
];

function errMsg(e: unknown, verb: string): string {
  if (e instanceof ApiError && (e.status === 401 || e.status === 403)) return `Not ${verb}: your session has expired. Refresh and try again.`;
  return e instanceof ApiError ? `Not ${verb}: ${e.message}` : `Not ${verb}. Try again.`;
}

function Switch({ on, onChange, label, disabled }: { on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={() => onChange(!on)}
      className="inline-flex h-11 w-16 shrink-0 items-center justify-center disabled:opacity-50">
      <span className={cn('relative inline-flex h-8 w-14 items-center rounded-full border transition-colors', on ? 'border-success bg-success' : 'border-border-default bg-inset')}>
        <span className={cn('inline-block h-6 w-6 rounded-full bg-white shadow transition-transform', on ? 'translate-x-7' : 'translate-x-1')} />
      </span>
    </button>
  );
}

function Stepper({ value, min, max, onChange, label }: { value: number; min: number; max: number; onChange: (v: number) => void; label: string }) {
  const v = Number.isFinite(value) ? value : min;
  return (
    <div className="inline-flex items-center rounded-md border border-border-default bg-surface" role="group" aria-label={label}>
      <button type="button" aria-label={`Decrease ${label}`} disabled={v <= min} onClick={() => onChange(Math.max(min, v - 1))} className="h-11 w-11 text-lg text-text-primary disabled:opacity-40">−</button>
      <input aria-label={label} inputMode="numeric" value={Number.isFinite(value) ? value : ''} onChange={(e) => onChange(e.target.value === '' ? NaN : Number(e.target.value))}
        className="h-11 w-14 border-x border-border-default bg-transparent text-center font-mono text-sm tabular-nums text-text-primary outline-none" />
      <button type="button" aria-label={`Increase ${label}`} disabled={v >= max} onClick={() => onChange(Math.min(max, v + 1))} className="h-11 w-11 text-lg text-text-primary disabled:opacity-40">+</button>
    </div>
  );
}

/** Status pill with >=4.5:1 text (primary ink + a coloured dot; the Badge token is below 4.5:1). */
function StatePill({ running }: { running: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border-default bg-surface px-2 py-0.5 text-xs font-medium text-text-primary">
      <span aria-hidden className={cn('h-2 w-2 rounded-full', running ? 'bg-success' : 'bg-warning')} />
      {running ? 'Running · drafts only' : 'Stopped'}
    </span>
  );
}

/** True once the element has been on screen (fetch only when the panel is visible — Delta UI IVG R1). */
function useSeenOnce<T extends HTMLElement>(start: boolean): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(start);
  useEffect(() => {
    if (seen) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setSeen(true); return; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect(); } });
    io.observe(el);
    return () => io.disconnect();
  }, [seen]);
  return [ref, seen];
}

function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: 'danger' }) {
  return (
    <div className="min-w-0 rounded-md border border-border-subtle bg-canvas px-3 py-2">
      <div className="text-[11px] font-medium uppercase tracking-wide text-text-secondary">{label}</div>
      <div className={cn('mt-0.5 font-mono text-lg tabular-nums text-text-primary', tone === 'danger' && 'text-danger')}>{value}</div>
    </div>
  );
}

function SettingRow({ label, help, unit, children }: { label: string; help: string; unit?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 border-b border-border-subtle py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="text-sm font-medium text-text-primary">{label}</div>
        <p className="text-xs text-text-secondary">{help}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">{children}{unit && <span className="text-xs text-text-secondary">{unit}</span>}</div>
    </div>
  );
}

/** The SEO autopilot console. `expanded` opens the tabs straight away (Library drawer). */
export function SeoAutopilotConsole({ expanded = false }: { expanded?: boolean }) {
  const qc = useQueryClient();
  const [seenRef, seen] = useSeenOnce<HTMLElement>(expanded);
  const status = useQuery({ queryKey: KEY, queryFn: getSeoAutopilot, retry: false, staleTime: 60_000, enabled: seen });
  const [open, setOpen] = useState(expanded);
  const [tab, setTab] = useState<Tab>('overview');
  const [draft, setDraft] = useState<SeoAutopilotSettings | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { if (status.data) setDraft(status.data.settings); }, [status.data]);

  const save = useMutation({
    mutationFn: (patch: Partial<SeoAutopilotSettings>) => saveSeoAutopilotSettings(patch),
    onSuccess: (res) => { qc.setQueryData(KEY, res); setDraft(res.settings); setMsg('Saved.'); },
    onError: (e) => setMsg(errMsg(e, 'saved')),
  });
  const resume = useMutation({
    mutationFn: (listingId: string) => resumeSeoAutopilotListing(listingId),
    onSuccess: (res) => { qc.setQueryData(KEY, res); setMsg('Page resumed.'); },
    onError: (e) => setMsg(errMsg(e, 'resumed')),
  });

  const s = status.data;
  const err = draft ? settingsError(draft) : null;
  const patch = s && draft ? settingsPatch(s.settings, draft) : {};
  const dirty = Object.keys(patch).length > 0;

  const t = todayParts();
  const todayIso = iso(t);
  const running = !!s && s.running;
  const envStops = s ? s.stops.filter((x) => x !== 'autopilot_global_stop_on') : [];

  // The big switch saves ONLY global_stop straight away (the same POST + audit as before).
  const toggleRunning = (on: boolean) => {
    if (!s) return;
    if (dirty) { setMsg('Save or reset your other changes first.'); setOpen(true); return; }
    if (!on && !window.confirm('Stop the autopilot? No new drafts are written until you switch it back on.')) return;
    setMsg(null);
    save.mutate({ global_stop: !on });
  };

  // R1: no error boxes. 403 (not an owner/operator) -> render nothing; 503 / other -> one quiet line.
  const httpStatus = status.error instanceof ApiError ? status.error.status : null;
  if (status.isError && httpStatus === 403) return null;
  if (status.isError) {
    return (
      <p ref={seenRef as React.RefObject<HTMLParagraphElement>} className="text-sm text-text-secondary" role="status">
        SEO autopilot unavailable right now.{httpStatus !== 503 && <> <button className="inline-flex min-h-[44px] items-center font-medium text-ai underline" onClick={() => void status.refetch()}>Try again</button></>}
      </p>
    );
  }

  const onTabKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const tabsEls = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role=tab]'));
    const focused = tabsEls.indexOf(document.activeElement as HTMLElement);
    const i = focused >= 0 ? focused : TABS.findIndex((x) => x.key === tab);
    const n = e.key === 'ArrowRight' ? (i + 1) % TABS.length : e.key === 'ArrowLeft' ? (i - 1 + TABS.length) % TABS.length : e.key === 'Home' ? 0 : e.key === 'End' ? TABS.length - 1 : -1;
    if (n < 0) return;
    e.preventDefault();
    setTab(TABS[n].key);
    tabsEls[n]?.focus();
  };

  return (
    <section ref={seenRef} className="rounded-xl border border-border-subtle bg-surface" aria-label="SEO autopilot settings" id="seo-autopilot">
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-lg font-semibold text-text-primary">SEO autopilot</h3>
            {s && <StatePill running={running} />}
          </div>
          <p className="mt-0.5 text-sm text-text-secondary">Writes blog drafts for your listing pages and saves them as WordPress drafts. Nothing is published without you.</p>
        </div>
        {s && draft && (
          <div className="flex items-center gap-3 rounded-lg border border-border-subtle bg-canvas px-3 py-2">
            <div className="text-right">
              <div className="text-sm font-semibold text-text-primary">{running ? 'Running' : 'Stopped'}</div>
              <div className="text-[11px] text-text-secondary">{envStops.length ? 'Switched off on the server' : 'Stop the autopilot any time'}</div>
            </div>
            <Switch on={!draft.global_stop} disabled={save.isPending} onChange={toggleRunning} label={draft.global_stop ? 'Start the autopilot' : 'Stop the autopilot'} />
          </div>
        )}
      </div>

      {status.isLoading && <div className="px-4 pb-4"><LoadingState rows={2} /></div>}

      {s && draft && (
        <>
          <div className="grid grid-cols-2 gap-2 px-4 sm:grid-cols-4">
            <Stat label="Drafts today" value={scheduleToday(s.schedule, todayIso)} />
            <Stat label="This week" value={s.schedule ? s.schedule.weekly_quota : '—'} />
            <Stat label="Est. monthly cost" value={s.cost_estimate?.per_month ?? '—'} />
            <Stat label="Needs attention" value={s.alerts.length} tone={s.alerts.length ? 'danger' : undefined} />
          </div>
          {envStops.length > 0 && (
            <div className="mx-4 mt-3 flex flex-wrap gap-1.5">{envStops.map((x) => <Badge key={x} tone="warning">{STOP_LABELS[x] ?? x}</Badge>)}</div>
          )}
          <div className="flex items-center justify-between gap-2 px-4 py-3">
            {msg && !open ? <span role="status" className="text-xs text-text-primary">{msg}</span> : <span />}
            <Button type="button" variant="secondary" className="min-h-[44px]" aria-expanded={open} onClick={() => setOpen((v) => !v)}>{open ? 'Close autopilot' : 'Open autopilot'}</Button>
          </div>

          {open && (
            <div className="border-t border-border-subtle">
              <div role="tablist" aria-label="SEO autopilot sections" onKeyDown={onTabKey} className="flex gap-1 overflow-x-auto px-3 pt-2 [scrollbar-width:none]">
                {TABS.map((x) => (
                  <button key={x.key} role="tab" aria-selected={tab === x.key} tabIndex={tab === x.key ? 0 : -1} onClick={() => setTab(x.key)}
                    className={cn('min-h-[44px] shrink-0 border-b-2 px-3 text-sm', tab === x.key ? 'border-ai font-semibold text-ai' : 'border-transparent text-text-secondary hover:text-text-primary')}>
                    {x.label}{x.key === 'activity' && s.alerts.length ? ` (${s.alerts.length})` : ''}
                  </button>
                ))}
              </div>
              <div className="p-4" role="tabpanel">
                {tab === 'overview' && <Overview s={s} draft={draft} go={setTab} />}
                {tab === 'schedule' && <Schedule s={s} todayIso={todayIso} />}
                {tab === 'pages' && <Pages s={s} draft={draft} setDraft={setDraft} onResume={(id) => { setMsg(null); resume.mutate(id); }} resuming={resume.isPending} />}
                {tab === 'settings' && <Settings s={s} draft={draft} setDraft={setDraft} />}
                {tab === 'activity' && <Activity s={s} onResume={(id) => { setMsg(null); resume.mutate(id); }} resuming={resume.isPending} />}
              </div>
              {(dirty || msg) && (
                <div className="sticky bottom-0 flex flex-wrap items-center gap-2 border-t border-border-default bg-surface/95 px-4 py-3 backdrop-blur">
                  <span role="status" className={cn('min-w-0 flex-1 text-sm', err ? 'text-danger' : 'text-text-primary')}>
                    {err ?? (dirty ? 'You have unsaved changes.' : msg)}
                  </span>
                  {dirty && <Button type="button" variant="ghost" disabled={save.isPending} onClick={() => { setDraft(s.settings); setMsg(null); }}>Reset</Button>}
                  {dirty && <Button type="button" variant="primary" disabled={!!err || save.isPending} onClick={() => { setMsg(null); save.mutate(patch); }}>{save.isPending ? 'Saving…' : 'Save settings'}</Button>}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Overview({ s, draft, go }: { s: SeoAutopilotStatus; draft: SeoAutopilotSettings; go: (t: Tab) => void }) {
  const active = s.listings.filter((l) => pageState(l, draft) === 'active').length;
  const c = s.cost_estimate;
  return (
    <div className="space-y-4 text-sm">
      <div className="grid gap-3 sm:grid-cols-3">
        <button onClick={() => go('pages')} className="rounded-lg border border-border-subtle p-3 text-left hover:border-ai">
          <div className="text-xs text-text-secondary">Pages it writes for</div>
          <div className="font-mono text-xl tabular-nums text-text-primary">{active}</div>
          <div className="text-xs text-text-secondary">of {s.listings.length} checked · see Pages</div>
        </button>
        <button onClick={() => go('schedule')} className="rounded-lg border border-border-subtle p-3 text-left hover:border-ai">
          <div className="text-xs text-text-secondary">Next 7 days</div>
          <div className="font-mono text-xl tabular-nums text-text-primary">{s.schedule ? s.schedule.preview.reduce((n, d) => n + d.items.length, 0) : 0}</div>
          <div className="text-xs text-text-secondary">drafts planned, {s.schedule?.daily_effective ?? 0} a day at most · see Schedule</div>
        </button>
        <button onClick={() => go('activity')} className="rounded-lg border border-border-subtle p-3 text-left hover:border-ai">
          <div className="text-xs text-text-secondary">Needs attention</div>
          <div className={cn('font-mono text-xl tabular-nums', s.alerts.length ? 'text-danger' : 'text-text-primary')}>{s.alerts.length}</div>
          <div className="text-xs text-text-secondary">rejections and pauses · see Activity</div>
        </button>
      </div>
      {s.schedule?.warnings.map((w) => <p key={w} role="status" className="text-sm text-danger">{w}</p>)}
      {c && (
        <div className="rounded-lg border border-border-subtle p-3">
          <div className="font-medium text-text-primary">What it costs (estimate, not a quote)</div>
          <p className="mt-1 text-text-secondary">About <b className="text-text-primary">{c.per_month ?? '—'}</b> a month ({c.per_article ?? '—'} per article, worst case {c.per_month_worst ?? '—'} a month) for about {c.posts_per_week} drafts a week across {c.eligible_listings} active pages.</p>
          <p className="mt-1 text-xs text-text-secondary">Based on {c.basis}. Keyword/SERP data: {c.evidence_api}.{c.fixed_weekly ? ` Fixed: ${c.fixed_weekly}.` : ''}</p>
        </div>
      )}
      <div className="text-xs text-text-secondary">
        {s.tracking && <p>Rank tracking: {s.tracking.added} listing keyword{s.tracking.added === 1 ? '' : 's'} added (limit {s.tracking.max}).</p>}
        {s.plan_at && <p>Pages last checked {formatDayTime(s.plan_at)} UTC.</p>}
        <p>Full automatic publishing is off. It becomes available after your trial review.</p>
      </div>
    </div>
  );
}

function Schedule({ s, todayIso }: { s: SeoAutopilotStatus; todayIso: string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const grid = width >= 900;
  const byId = useMemo(() => new Map(s.listings.map((l) => [l.listing_id, l] as const)), [s.listings]);
  if (!s.schedule || s.schedule.preview.length === 0) return <p className="text-sm text-text-secondary">No schedule yet. It appears after the first daily page check.</p>;
  return (
    <div ref={ref} className="space-y-2">
      <p className="text-sm font-medium text-text-primary">Next 7 days — {s.schedule.daily_effective} draft{s.schedule.daily_effective === 1 ? '' : 's'} a day at most, {s.schedule.weekly_quota} a week</p>
      {s.schedule.warnings.map((w) => <p key={w} className="text-xs text-danger" role="status">{w}</p>)}
      <div className={grid ? 'grid grid-cols-7 gap-2' : 'flex flex-col gap-3'}>
        {s.schedule.preview.map((d) => (
          <div key={d.date} className={cn('min-w-0 rounded-lg border p-2', d.date === todayIso ? 'border-ai bg-ai/5' : 'border-border-subtle')}>
            <div className="mb-1.5 flex items-center justify-between text-xs">
              <span className="font-semibold text-text-primary">{dayLabel(d.date, d.day)}</span>
              {d.date === todayIso ? <Badge tone="ai">Today</Badge> : <span className="font-mono text-text-secondary">{d.items.length}</span>}
            </div>
            <ul className="space-y-1.5">
              {d.items.map((it) => {
                const l = byId.get(it.listing_id);
                const url = l?.listing_url;
                const kind = pageKind(url, it.listing_id);
                return (
                  <li key={it.listing_id} className="rounded-md border border-border-subtle bg-canvas p-2 text-xs">
                    {url ? <a href={url} target="_blank" rel="noopener noreferrer" className="block truncate font-medium text-text-primary hover:text-ai" title={it.title}>{it.title}</a>
                      : <span className="block truncate font-medium text-text-primary" title={it.title}>{it.title}</span>}
                    <div className="mt-1 flex flex-wrap gap-1">
                      {it.primary && <span className="max-w-full truncate rounded-full bg-ai/10 px-2 py-0.5 text-[11px] text-ai" title={it.primary}>{it.primary}</span>}
                      <Badge tone={kind === 'archive' ? 'warning' : 'neutral'}>{kind === 'archive' ? 'Category page' : 'Listing'}</Badge>
                    </div>
                  </li>
                );
              })}
              {d.items.length === 0 && <li className="text-xs text-text-secondary">No drafts</li>}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function Pages({ s, draft, setDraft, onResume, resuming }: {
  s: SeoAutopilotStatus; draft: SeoAutopilotSettings; setDraft: (d: SeoAutopilotSettings) => void; onResume: (id: string) => void; resuming: boolean;
}) {
  const [q, setQ] = useState('');
  const [state, setState] = useState<PageState | 'all'>('all');
  const [sort, setSort] = useState<PageSort>('page');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');
  const [ref, width] = useWidth<HTMLDivElement>();
  const next = useMemo(() => nextDraftDates(s.schedule), [s.schedule]);
  const rows = filterSortPages(s.listings, draft, { q, state, sort, dir }, next);
  const tenants = Array.from(new Set(s.listings.map((l) => l.tenant_slug).filter(Boolean))).sort();
  const th = (k: PageSort, label: string) => (
    <th aria-sort={sort === k ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'} className="px-2 py-2 font-semibold">
      <button onClick={() => { if (sort === k) setDir(dir === 'asc' ? 'desc' : 'asc'); else { setSort(k); setDir('asc'); } }} className="inline-flex items-center gap-1 hover:text-text-primary">
        {label}{sort === k ? (dir === 'asc' ? ' ▲' : ' ▼') : ''}
      </button>
    </th>
  );
  const pauseListing = (id: string) => setDraft({ ...draft, paused_listings: toggleIn(draft.paused_listings, id) });
  return (
    <div ref={ref} className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <input type="search" aria-label="Search pages" placeholder="Search page, tenant or keyword…" value={q} onChange={(e) => setQ(e.target.value)}
          className="h-11 min-w-0 flex-1 rounded-md border border-border-default bg-surface px-3 text-sm sm:h-9" />
        <select aria-label="Page status" value={state} onChange={(e) => setState(e.target.value as PageState | 'all')} className="h-11 rounded-md border border-border-default bg-surface px-2 text-sm sm:h-9">
          <option value="all">All pages ({s.listings.length})</option>
          {(Object.keys(PAGE_STATE_LABEL) as PageState[]).map((k) => <option key={k} value={k}>{PAGE_STATE_LABEL[k]}</option>)}
        </select>
        {width < 900 && (
          <select aria-label="Sort pages" value={sort} onChange={(e) => setSort(e.target.value as PageSort)} className="h-11 rounded-md border border-border-default bg-surface px-2 text-sm sm:h-9">
            <option value="page">Sort: page</option><option value="position">Sort: Google position</option><option value="next">Sort: next draft</option><option value="tenant">Sort: tenant</option><option value="keyword">Sort: keyword</option>
          </select>
        )}
      </div>
      {width >= 900 ? (
        <div className="overflow-x-auto rounded-md border border-border-subtle">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border-subtle text-left text-[11px] uppercase tracking-wide text-text-secondary">
              {th('page', 'Page')}{th('tenant', 'Tenant')}{th('keyword', 'Keyword')}{th('position', 'Google')}
              <th className="px-2 py-2 font-semibold">Cadence</th>{th('next', 'Next draft')}<th className="px-2 py-2 font-semibold">Status</th><th className="px-2 py-2 font-semibold">Writing</th>
            </tr></thead>
            <tbody>
              {rows.map((l) => {
                const st = pageState(l, draft);
                return (
                  <tr key={l.listing_id} className="border-b border-border-subtle align-top last:border-0">
                    <td className="max-w-[260px] px-2 py-2">
                      {l.listing_url ? <a href={l.listing_url} target="_blank" rel="noopener noreferrer" className="block truncate font-medium text-text-primary hover:text-ai" title={l.listing_title}>{l.listing_title}</a> : <span className="block truncate font-medium">{l.listing_title}</span>}
                      <span className="block truncate text-[11px] text-text-secondary">{pagePath(l.listing_url)} · {pageKind(l.listing_url, l.listing_id) === 'archive' ? 'Category page' : 'Listing'}</span>
                    </td>
                    <td className="px-2 py-2 text-xs text-text-secondary">{l.tenant_slug || '—'}</td>
                    <td className="max-w-[180px] truncate px-2 py-2 text-xs" title={l.keyword}>{l.keyword || '—'}</td>
                    <td className="px-2 py-2 font-mono text-xs tabular-nums">{typeof l.position === 'number' ? l.position.toFixed(1) : '—'}</td>
                    <td className="px-2 py-2 text-xs text-text-secondary">{l.eligible ? tierLabel(l.tier, draft.cadence) : reasonLabel(l.reason)}</td>
                    <td className="px-2 py-2 text-xs">{next[l.listing_id] ? dayLabel(next[l.listing_id]) : '—'}</td>
                    <td className="px-2 py-2"><Badge tone={st === 'active' ? 'success' : st === 'not_eligible' ? 'neutral' : 'warning'}>{PAGE_STATE_LABEL[st]}</Badge>
                      {l.auto_paused && <Button type="button" variant="secondary" className="ml-1 h-7 px-2 text-xs" disabled={resuming} onClick={() => onResume(l.listing_id)}>Resume</Button>}
                    </td>
                    <td className="px-2 py-2">{l.eligible && <Switch on={!draft.paused_listings.includes(l.listing_id)} onChange={() => pauseListing(l.listing_id)} label={`Write for ${l.listing_title}`} />}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((l) => {
            const st = pageState(l, draft);
            return (
              <li key={l.listing_id} className="rounded-md border border-border-subtle p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    {l.listing_url ? <a href={l.listing_url} target="_blank" rel="noopener noreferrer" className="block truncate text-sm font-medium text-text-primary">{l.listing_title}</a> : <span className="block truncate text-sm font-medium">{l.listing_title}</span>}
                    <span className="block truncate text-[11px] text-text-secondary">{l.tenant_slug} · {pageKind(l.listing_url, l.listing_id) === 'archive' ? 'Category page' : 'Listing'}</span>
                  </div>
                  {l.eligible && <Switch on={!draft.paused_listings.includes(l.listing_id)} onChange={() => pauseListing(l.listing_id)} label={`Write for ${l.listing_title}`} />}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-text-secondary">
                  {l.keyword && <span className="rounded-full bg-ai/10 px-2 py-0.5 text-ai">{l.keyword}</span>}
                  <span>Google: {typeof l.position === 'number' ? l.position.toFixed(1) : '—'}</span>
                  <span>{l.eligible ? tierLabel(l.tier, draft.cadence) : reasonLabel(l.reason)}</span>
                  <span>Next: {next[l.listing_id] ? dayLabel(next[l.listing_id]) : '—'}</span>
                  <Badge tone={st === 'active' ? 'success' : st === 'not_eligible' ? 'neutral' : 'warning'}>{PAGE_STATE_LABEL[st]}</Badge>
                </div>
                {l.auto_paused && <Button type="button" variant="secondary" className="mt-2 min-h-[44px]" disabled={resuming} onClick={() => onResume(l.listing_id)}>Resume this page</Button>}
              </li>
            );
          })}
        </ul>
      )}
      {rows.length === 0 && <p className="text-sm text-text-secondary">{s.listings.length ? 'No pages match.' : 'No page check has run yet.'}</p>}
      {tenants.length > 0 && (
        <div className="rounded-lg border border-border-subtle p-3">
          <div className="text-sm font-medium text-text-primary">Pause a whole partner</div>
          <p className="text-xs text-text-secondary">Stops drafts for every page of that partner until you switch it back on.</p>
          <div className="mt-2 flex flex-wrap gap-3">
            {tenants.map((tn) => (
              <label key={tn} className="flex min-h-[44px] items-center gap-2 text-sm text-text-primary">
                <Switch on={!draft.paused_tenants.includes(tn)} onChange={() => setDraft({ ...draft, paused_tenants: toggleIn(draft.paused_tenants, tn) })} label={`Write for ${tn}`} />
                {tn}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Settings({ s, draft, setDraft }: { s: SeoAutopilotStatus; draft: SeoAutopilotSettings; setDraft: (d: SeoAutopilotSettings) => void }) {
  const [adv, setAdv] = useState(false);
  const setCap = (k: 'per_day' | 'per_week' | 'per_month', v: number) => setDraft({ ...draft, caps: { ...draft.caps, [k]: v } });
  const setCad = (k: keyof SeoAutopilotCadence, v: number) => setDraft({ ...draft, cadence: { ...draft.cadence, [k]: v } });
  const keys = Object.keys(CADENCE_LIMITS) as Array<keyof SeoAutopilotCadence>;
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border-subtle px-3">
        <h4 className="pt-3 text-sm font-semibold text-text-primary">How much to write</h4>
        <SettingRow label="Daily cap follows the listings" help={`Spread the week evenly across your pages${s.schedule ? ` (now ${s.schedule.daily_auto} a day for ${s.schedule.weekly_quota} a week)` : ''}. Switch off to set your own daily limit.`}>
          <Switch on={draft.caps.daily_auto} onChange={(v) => setDraft({ ...draft, caps: { ...draft.caps, daily_auto: v } })} label="Daily cap follows the listings" />
        </SettingRow>
        {!draft.caps.daily_auto && (
          <SettingRow label="How many drafts per day at most" help="The autopilot never writes more than this in one day." unit="a day">
            <Stepper value={draft.caps.per_day} min={0} max={5000} onChange={(v) => setCap('per_day', v)} label="Max drafts per day" />
          </SettingRow>
        )}
        <SettingRow label="How many drafts per week at most" help="A hard ceiling across all pages." unit="a week">
          <Stepper value={draft.caps.per_week} min={0} max={5000} onChange={(v) => setCap('per_week', v)} label="Max drafts per week" />
        </SettingRow>
        <SettingRow label="How many drafts per month at most" help="Keeps the monthly cost predictable." unit="a month">
          <Stepper value={draft.caps.per_month} min={0} max={5000} onChange={(v) => setCap('per_month', v)} label="Max drafts per month" />
        </SettingRow>
      </div>
      <div className="rounded-lg border border-border-subtle px-3">
        <h4 className="pt-3 text-sm font-semibold text-text-primary">How often, by Google position</h4>
        <p className="text-xs text-text-secondary">Pages that rank lower get drafts more often.</p>
        {keys.filter((k) => !CADENCE_PLAIN[k].advanced).map((k) => (
          <SettingRow key={k} label={CADENCE_PLAIN[k].label} help={CADENCE_PLAIN[k].help} unit={CADENCE_PLAIN[k].unit}>
            <Stepper value={draft.cadence[k]} min={CADENCE_LIMITS[k][0]} max={CADENCE_LIMITS[k][1]} onChange={(v) => setCad(k, v)} label={CADENCE_PLAIN[k].label} />
          </SettingRow>
        ))}
      </div>
      <div className="rounded-lg border border-border-subtle px-3">
        <button type="button" aria-expanded={adv} onClick={() => setAdv((v) => !v)} className="flex min-h-[44px] w-full items-center justify-between text-sm font-semibold text-text-primary">
          Advanced: when pages speed up, slow down or pause <span aria-hidden>{adv ? '▴' : '▾'}</span>
        </button>
        {adv && keys.filter((k) => CADENCE_PLAIN[k].advanced).map((k) => (
          <SettingRow key={k} label={CADENCE_PLAIN[k].label} help={CADENCE_PLAIN[k].help} unit={CADENCE_PLAIN[k].unit}>
            <Stepper value={draft.cadence[k]} min={CADENCE_LIMITS[k][0]} max={CADENCE_LIMITS[k][1]} onChange={(v) => setCad(k, v)} label={CADENCE_PLAIN[k].label} />
          </SettingRow>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border-subtle p-3" title="Prepared for after the review trial; not available yet">
        <div>
          <div className="text-sm font-medium text-text-primary">Full automatic publishing</div>
          <p className="text-xs text-text-secondary">Off. Available after your trial review. Until then every draft waits for you in WordPress.</p>
        </div>
        <Switch on={false} onChange={() => undefined} disabled label="Full automatic publishing (not available yet)" />
      </div>
    </div>
  );
}

function Activity({ s, onResume, resuming }: { s: SeoAutopilotStatus; onResume: (id: string) => void; resuming: boolean }) {
  const byId = new Map(s.listings.map((l) => [l.listing_id, l] as const));
  return (
    <div className="space-y-4">
      <div>
        <h4 className="mb-2 text-sm font-semibold text-text-primary">Needs attention</h4>
        {s.alerts.length === 0 ? <p className="text-sm text-text-secondary">Nothing needs your attention.</p> : (
          <ul className="space-y-2">
            {s.alerts.map((a, i) => {
              const l = a.listing_id ? byId.get(a.listing_id) : undefined;
              const { sentence, quote } = describeAlert(a.detail);
              return (
                <li key={i} className="rounded-md border border-border-subtle p-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-text-primary">{alertLabel(a.type)}</span>
                    <span className="font-mono text-xs text-text-secondary">{formatDayTime(a.at)}</span>
                  </div>
                  {l && <div className="text-xs text-text-secondary">{l.listing_title}</div>}
                  <p className="mt-1 text-sm text-text-primary">{sentence}</p>
                  {quote && <blockquote className="mt-1 border-l-2 border-warning pl-2 text-sm italic text-text-primary">“{quote}”</blockquote>}
                  {l?.auto_paused && <Button type="button" variant="secondary" className="mt-2 min-h-[44px]" disabled={resuming} onClick={() => onResume(l.listing_id)}>Resume this page</Button>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {s.audit.length > 0 && (
        <details className="rounded-md border border-border-subtle p-3 text-xs text-text-secondary">
          <summary className="flex min-h-[44px] cursor-pointer items-center text-sm font-medium text-text-primary">Change history ({s.audit.length})</summary>
          <ul className="mt-2 space-y-1">{s.audit.map((a, i) => <li key={i}>{formatDayTime(a.at)} · {describeChange(a.principal, Object.keys(a.changes))}</li>)}</ul>
        </details>
      )}
    </div>
  );
}
